/**
 * SavedIndicator — Sprint B.2.1
 * ─────────────────────────────
 * Subtle save-state chip shown under the wizard progress bar.
 * Reads from OnboardingSaveStatusContext; never mutates state.
 *
 *   • Saving…                 — write in flight
 *   • Saved just now          — completed <1 min ago
 *   • Saved X minutes ago     — older successes
 *   • Save failed             — only on real caught error path
 */

import { useEffect, useState } from "react";
import {
  describeSaveStatus,
  useOnboardingSaveStatus,
} from "@/data/contractor/onboarding/saveStatus";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Save as SaveIcon,
} from "lucide-react";

export function OnboardingSavedIndicator() {
  const status = useOnboardingSaveStatus();
  const [now, setNow] = useState<number>(() => Date.now());

  // Cheap ticker so "Saved X minutes ago" stays current without
  // re-rendering the entire wizard tree.
  useEffect(() => {
    if (status.kind !== "saved") return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [status.kind]);

  const label = describeSaveStatus(status, now);
  const isError = status.kind === "error";
  const isSaving = status.kind === "saving";
  const isSaved = status.kind === "saved";

  const Icon = isError
    ? AlertCircle
    : isSaving
      ? Loader2
      : isSaved
        ? CheckCircle2
        : SaveIcon;

  return (
    <div
      className="flex items-center gap-1.5 text-[10px] text-muted-foreground"
      data-testid="onboarding-wizard-saved-indicator"
      data-save-status={status.kind}
      aria-live="polite"
    >
      <Icon
        className={[
          "h-3 w-3 shrink-0",
          isError ? "text-destructive" : "",
          isSaving ? "text-muted-foreground animate-spin" : "",
          isSaved ? "text-success" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      />
      <span
        className={isError ? "text-destructive" : undefined}
        data-testid="onboarding-wizard-saved-indicator-label"
      >
        {label}
      </span>
    </div>
  );
}

export default OnboardingSavedIndicator;
